import React, { useState, useEffect, useMemo } from 'react';
import axios from './axiosConfig';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useConfirm } from '../context/ConfirmContext';

// Variantes de animación
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 }
  }
};

function Lineas() {
  const { canCreateEdit } = useAuth();
  const { confirm } = useConfirm();
  const [lineas, setLineas] = useState([]);
  const [universidades, setUniversidades] = useState([]);
  const [newLinea, setNewLinea] = useState({
    nombre: '',
    universidad_id: '',
    descripcion: '',
    estado: 'ACTIVO',
  });
  const [error, setError] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const uniById = useMemo(() => {
    const m = {};
    universidades.forEach((u) => {
      m[u.id] = u.name || u.short_name || `ID ${u.id}`;
    });
    return m;
  }, [universidades]);

  useEffect(() => {
    fetchLineas();
  }, []);

  useEffect(() => {
    axios
      .get('/api/universities')
      .then((res) => setUniversidades(Array.isArray(res.data) ? res.data : []))
      .catch(() => setUniversidades([]));
  }, []);

  const fetchLineas = async () => {
    try {
      const response = await axios.get('/api/lineas/');
      if (Array.isArray(response.data)) {
        setLineas(response.data);
        setError(null);
      } else {
        setLineas([]);
        setError('La respuesta del servidor no es una lista válida');
      }
    } catch (err) {
      setLineas([]);
      setError('Error al obtener líneas: ' + err.message);
      console.error('Error fetching lineas:', err);
    }
  };

  const handleInputChange = (e) => {
    setNewLinea({ ...newLinea, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newLinea.universidad_id) {
      setError('Selecciona la universidad (IES) de la línea.');
      return;
    }
    try {
      const universidad_id = parseInt(newLinea.universidad_id, 10);
      if (isEditing) {
        await axios.put(`/api/lineas/${editId}`, {
          nombre: newLinea.nombre,
          universidad_id,
          descripcion: newLinea.descripcion || null,
          estado: newLinea.estado,
        });
      } else {
        await axios.post('/api/lineas/', {
          nombre: newLinea.nombre,
          universidad_id,
          descripcion: newLinea.descripcion || null,
          estado: newLinea.estado,
        });
      }
      fetchLineas();
      resetForm();
      setShowFormModal(false);
      setError(null);
    } catch (err) {
      setError(
        `Error al ${isEditing ? 'actualizar' : 'agregar'} línea: ` +
          (err.response?.data?.detail || err.message)
      );
    }
  };

  const handleEdit = (linea) => {
    setNewLinea({
      nombre: linea.nombre,
      universidad_id: String(linea.universidad_id),
      descripcion: linea.descripcion || '',
      estado: linea.estado || 'ACTIVO',
    });
    setEditId(linea.linea_id);
    setIsEditing(true);
    setShowFormModal(true);
  };

  const handleDelete = async (lineaId) => {
    const accepted = await confirm({
      title: 'Eliminar línea de investigación',
      message: '¿Estás seguro de eliminar esta línea de investigación?',
      details: 'La línea dejará de estar disponible en el catálogo del sistema.',
      confirmText: 'Sí, eliminar',
      cancelText: 'Cancelar',
      variation: 'danger',
      badge: 'Eliminar',
      icon: 'delete',
    });
    if (!accepted) return;

    try {
      await axios.delete(`/api/lineas/${lineaId}`);
      fetchLineas();
      setError(null);
    } catch (err) {
      setError('Error al eliminar línea: ' + err.message);
    }
  };

  const resetForm = () => {
    setNewLinea({
      nombre: '',
      universidad_id: '',
      descripcion: '',
      estado: 'ACTIVO',
    });
    setIsEditing(false);
    setEditId(null);
  };

  const term = searchTerm.toLowerCase();
  const filteredLineas = lineas.filter((linea) => {
    const uniName = (uniById[linea.universidad_id] || '').toLowerCase();
    return (
      linea.nombre.toLowerCase().includes(term) ||
      (linea.descripcion && linea.descripcion.toLowerCase().includes(term)) ||
      uniName.includes(term)
    );
  });

  const totalPages = Math.ceil(filteredLineas.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedLineas = filteredLineas.slice(startIndex, startIndex + itemsPerPage);

  const iesConLineas = new Set(lineas.map((l) => l.universidad_id).filter(Boolean)).size;

  return (
    <>
      {/* Header */}
      <motion.header 
        className="bg-surface-light dark:bg-surface-dark shadow-sm border-b border-border-light dark:border-border-dark z-10"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="max-w-1xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div>
              <h2 className="text-xl font-bold leading-7 text-slate-900 dark:text-white sm:truncate">Líneas de Investigación</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Líneas por IES (tabla linea_investigacion)</p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="relative hidden md:block">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="material-icons-round text-slate-400 text-sm">search</span>
                </div>
                <input 
                  className="block w-64 pl-9 pr-3 py-2 border border-border-light dark:border-border-dark rounded-md leading-5 bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-sm transition-all shadow-sm" 
                  placeholder="Buscar línea..." 
                  type="text"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                />
              </div>
              {canCreateEdit() && (
                <motion.button 
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all" 
                  type="button"
                  onClick={() => { resetForm(); setShowFormModal(true); }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="material-icons-round text-sm mr-2">add</span>
                  Nueva Línea
                </motion.button>
              )}
            </div>
          </div>
        </div>
      </motion.header>

      <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 space-y-6">
        {/* Error Alert */}
        <AnimatePresence>
          {error && (
            <motion.div 
              className="bg-red-50 border border-red-200 rounded-md p-4 mb-6"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <div className="flex">
                <span className="material-icons-round text-red-400 mr-3">error</span>
                <div>
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <div className="text-sm text-red-700 mt-1">{error}</div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Estadísticas */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          <div className="flex items-center justify-between mb-4 px-1">
            <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Estadísticas Generales</h3>
          </div>
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-5"
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
          >
            <motion.div 
              className="glass-panel rounded-xl p-6 border border-white/20 shadow-lg dark:border-slate-700 relative overflow-hidden group"
              variants={fadeInUp}
              whileHover={{ scale: 1.02, y: -5 }}
              transition={{ duration: 0.2 }}
            >
              <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                <span className="material-icons-round text-6xl text-primary">psychology</span>
              </div>
              <div className="flex items-center mb-3">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                  Total Líneas
                </span>
              </div>
              <motion.h4 
                className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
              >
                {lineas.length}
              </motion.h4>
              <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-700/50 pt-3">
                <span className="text-xs font-medium text-primary">Líneas registradas</span>
              </div>
            </motion.div>
            <motion.div 
              className="glass-panel rounded-xl p-6 border border-white/20 shadow-lg dark:border-slate-700 relative overflow-hidden group"
              variants={fadeInUp}
              whileHover={{ scale: 1.02, y: -5 }}
              transition={{ duration: 0.2 }}
            >
              <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                <span className="material-icons-round text-6xl text-indigo-500">category</span>
              </div>
              <div className="flex items-center mb-3">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300">
                  IES con líneas
                </span>
              </div>
              <motion.h4 
                className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, delay: 0.3 }}
              >
                {iesConLineas}
              </motion.h4>
              <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-700/50 pt-3">
                <span className="text-xs font-medium text-indigo-600">Universidades distintas</span>
              </div>
            </motion.div>
            <motion.div 
              className="glass-panel rounded-xl p-6 border border-white/20 shadow-lg dark:border-slate-700 relative overflow-hidden group"
              variants={fadeInUp}
              whileHover={{ scale: 1.02, y: -5 }}
              transition={{ duration: 0.2 }}
            >
              <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                <span className="material-icons-round text-6xl text-purple-500">search</span>
              </div>
              <div className="flex items-center mb-3">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                  Resultados
                </span>
              </div>
              <motion.h4 
                className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, delay: 0.4 }}
              >
                {filteredLineas.length}
              </motion.h4>
              <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-700/50 pt-3">
                <span className="text-xs font-medium text-purple-600">Filtro activo</span>
              </div>
            </motion.div>
          </motion.div>
        </motion.section>

        {/* Tabla de Líneas */}
        <motion.section 
          className="bg-surface-light dark:bg-surface-dark shadow-md rounded-xl border border-border-light dark:border-border-dark overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="px-6 py-4 border-b border-border-light dark:border-border-dark flex items-center justify-between bg-slate-50/50 dark:bg-slate-800/50">
            <h3 className="text-base font-semibold text-slate-900 dark:text-white flex items-center">
              <span className="material-icons-round mr-2 text-slate-500">toc</span>
              Lista de Líneas de Investigación
            </h3>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300">
              {filteredLineas.length} registros
            </span>
          </div>
          
          {filteredLineas.length === 0 ? (
            <motion.div 
              className="text-center py-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <motion.span 
                className="material-icons-round text-slate-400 text-6xl mb-4 block"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                psychology
              </motion.span>
              <p className="text-slate-500 dark:text-slate-400">
                {searchTerm ? 'No se encontraron resultados para tu búsqueda' : 'No hay líneas de investigación disponibles.'}
              </p>
            </motion.div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border-light dark:divide-border-dark">
                <thead className="bg-slate-100 dark:bg-slate-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">ID</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Nombre</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">IES</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Estado</th>
                    <th className="px-6 py-3 text-right text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <motion.tbody 
                  className="bg-surface-light dark:bg-surface-dark divide-y divide-border-light dark:divide-border-dark"
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                >
                  {paginatedLineas.map((linea) => {
                    const lid = linea.linea_id || '';
                    const shortId = typeof lid === 'string' && lid.length > 10 ? `${lid.slice(0, 8)}…` : lid;
                    return (
                    <motion.tr 
                      key={linea.linea_id} 
                      className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                      variants={fadeInUp}
                      whileHover={{ backgroundColor: "rgba(59, 130, 246, 0.05)" }}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400 font-mono" title={lid}>{shortId}</td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-slate-900 dark:text-white">{linea.nombre}</div>
                        {linea.descripcion && (
                          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 truncate max-w-md">
                            {linea.descripcion.length > 120 ? linea.descripcion.slice(0, 120) + '...' : linea.descripcion}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700 dark:text-slate-300">
                        {uniById[linea.universidad_id] || `ID ${linea.universidad_id}`}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          linea.estado === 'ACTIVO'
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                            : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
                        }`}>
                          {linea.estado || 'ACTIVO'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          {canCreateEdit() && (
                            <>
                              <motion.button 
                                onClick={() => handleEdit(linea)}
                                className="text-amber-500 hover:text-amber-700 dark:hover:text-amber-400 bg-amber-100 dark:bg-amber-900/20 p-1.5 rounded-md transition-colors"
                                title="Editar"
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                              >
                                <span className="material-icons-round text-lg">edit</span>
                              </motion.button>
                              <motion.button 
                                onClick={() => handleDelete(linea.linea_id)}
                                className="text-rose-500 hover:text-rose-700 dark:hover:text-rose-400 bg-rose-100 dark:bg-rose-900/20 p-1.5 rounded-md transition-colors"
                                title="Eliminar"
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                              >
                                <span className="material-icons-round text-lg">delete</span>
                              </motion.button>
                            </>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                    );
                  })}
                </motion.tbody>
              </table>
            </div>
          )}
        </motion.section>

        {/* Paginación */}
        {totalPages > 1 && (
          <motion.div 
            className="flex justify-center"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
              <motion.button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-border-light dark:border-border-dark bg-white dark:bg-slate-800 text-sm font-medium text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="material-icons-round text-base">first_page</span>
              </motion.button>
              <motion.button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-2 py-2 border border-border-light dark:border-border-dark bg-white dark:bg-slate-800 text-sm font-medium text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="material-icons-round text-base">chevron_left</span>
              </motion.button>
              <span className="relative inline-flex items-center px-4 py-2 border border-border-light dark:border-border-dark bg-white dark:bg-slate-800 text-sm font-medium text-slate-700 dark:text-slate-300">
                Página {currentPage} de {totalPages}
              </span>
              <motion.button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-2 py-2 border border-border-light dark:border-border-dark bg-white dark:bg-slate-800 text-sm font-medium text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="material-icons-round text-base">chevron_right</span>
              </motion.button>
              <motion.button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-border-light dark:border-border-dark bg-white dark:bg-slate-800 text-sm font-medium text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="material-icons-round text-base">last_page</span>
              </motion.button>
            </nav>
          </motion.div>
        )}
      </div>

      {/* Modal */}
      <AnimatePresence>
        {showFormModal && (
          <motion.div 
            className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div 
              className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white dark:bg-slate-800"
              initial={{ opacity: 0, scale: 0.9, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: -20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
                  <motion.span 
                    className="material-icons-round mr-2"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    transition={{ delay: 0.1 }}
                  >
                    {isEditing ? 'edit' : 'add'}
                  </motion.span>
                  {isEditing ? 'Editar Línea' : 'Nueva Línea'}
                </h3>
                <motion.button
                  onClick={() => setShowFormModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <span className="material-icons-round">close</span>
                </motion.button>
              </div>
              <div className="p-4">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre *</label>
                    <input
                      type="text"
                      name="nombre"
                      value={newLinea.nombre}
                      onChange={handleInputChange}
                      required
                      placeholder="Nombre de la línea de investigación"
                      className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:outline-none focus:ring-primary focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Universidad (IES) *</label>
                    <select
                      name="universidad_id"
                      value={newLinea.universidad_id}
                      onChange={handleInputChange}
                      required
                      className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:outline-none focus:ring-primary focus:border-primary"
                    >
                      <option value="">Seleccionar IES</option>
                      {universidades.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name || u.short_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Estado</label>
                    <select
                      name="estado"
                      value={newLinea.estado}
                      onChange={handleInputChange}
                      className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:outline-none focus:ring-primary focus:border-primary"
                    >
                      <option value="ACTIVO">ACTIVO</option>
                      <option value="INACTIVO">INACTIVO</option>
                    </select>
                  </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Descripción</label>
                  <textarea
                    name="descripcion"
                    value={newLinea.descripcion}
                    onChange={handleInputChange}
                    rows="4"
                    placeholder="Descripción opcional de la línea..."
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:outline-none focus:ring-primary focus:border-primary"
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <motion.button 
                    type="button" 
                    onClick={() => setShowFormModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Cancelar
                  </motion.button>
                  <motion.button 
                    type="submit"
                    className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-blue-600 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span className="material-icons-round text-sm mr-2">{isEditing ? 'save' : 'add'}</span>
                    {isEditing ? 'Actualizar' : 'Agregar'}
                  </motion.button>
                </div>
              </form>
            </div>
          </motion.div>
        </motion.div>
        )}
      </AnimatePresence>

    </>
  );
}

export default Lineas;
